import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { posService } from '../../../lib/posService';

const PaymentConfigModal = ({ gymId, configs, onClose, onConfigsChange }) => {
  const [formData, setFormData] = useState({
    provider_name: '',
    is_enabled: true,
    requires_manual_entry: true
  });
  const [saving, setSaving] = useState(false);
  const [existingConfigs, setExistingConfigs] = useState(configs || []);

  useEffect(() => {
    setExistingConfigs(configs || []);
  }, [configs]);

  const handleAdd = async () => {
    if (!formData?.provider_name?.trim()) {
      alert('Please enter a provider name');
      return;
    }

    setSaving(true);
    const { error } = await posService?.createCreditCardConfig(gymId, formData);
    
    if (error) {
      alert(error?.message || 'Failed to add payment configuration');
    } else {
      setFormData({
        provider_name: '',
        is_enabled: true,
        requires_manual_entry: true
      });
      onConfigsChange();
    }
    setSaving(false);
  };

  const handleToggle = async (configId, currentStatus) => {
    const { error } = await posService?.updateCreditCardConfig(configId, {
      is_enabled: !currentStatus
    });
    
    if (error) {
      alert(error?.message || 'Failed to update configuration');
    } else {
      onConfigsChange();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Payment Settings</h2>
            <p className="text-sm text-muted-foreground mt-1">Configure credit card payment options</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {/* Add New Configuration */}
          <div className="bg-muted/20 rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-4">Add Payment Method</h3>
            <div className="space-y-4">
              <Input
                label="Provider Name"
                type="text"
                placeholder="e.g., Manual Entry, Square Terminal, Custom POS"
                value={formData?.provider_name}
                onChange={(e) => setFormData({ ...formData, provider_name: e?.target?.value })}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="manual-entry"
                  checked={formData?.requires_manual_entry}
                  onChange={(e) => setFormData({ ...formData, requires_manual_entry: e?.target?.checked })}
                  className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
                <label htmlFor="manual-entry" className="text-sm text-foreground">
                  Requires manual entry of last 4 digits
                </label>
              </div>
              <Button
                onClick={handleAdd}
                disabled={saving}
                iconName="Plus"
                iconPosition="left"
                size="sm"
              >
                {saving ? 'Adding...' : 'Add Payment Method'}
              </Button>
            </div>
          </div>

          {/* Existing Configurations */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Configured Payment Methods</h3>
            {existingConfigs?.length === 0 ? (
              <div className="text-center py-8 bg-muted/20 rounded-lg">
                <Icon name="CreditCard" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No payment methods configured</p>
              </div>
            ) : (
              <div className="space-y-3">
                {existingConfigs?.map(config => (
                  <div key={config?.id} className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        config?.is_enabled ? 'bg-success/10' : 'bg-muted'
                      }`}>
                        <Icon 
                          name="CreditCard" 
                          size={20} 
                          color={config?.is_enabled ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
                        />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{config?.provider_name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {config?.requires_manual_entry ? 'Manual entry required' : 'Automated processing'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggle(config?.id, config?.is_enabled)}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-colors
                        ${config?.is_enabled
                          ? 'bg-success text-success-foreground hover:bg-success/90'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                        }
                      `}
                    >
                      {config?.is_enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">Payment Configuration Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Credit card payments require at least one enabled payment method</li>
                  <li>Manual entry mode requires staff to enter the last 4 digits of the card</li>
                  <li>This system does not integrate with Stripe or other payment processors</li>
                  <li>All transactions are recorded for reporting and audit purposes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-end">
          <Button onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfigModal;